
    import React, { useState } from 'react';
    import { useNavigate } from 'react-router-dom';
    import { useDolphin } from '../../hooks/useDolphin';
    import { useWallet } from '../../context/WalletContext';
    import ConnectPrompt from '../../components/common/ConnectPrompt';
    import { toast } from 'react-toastify';
    import { FaHammer, FaInfoCircle } from 'react-icons/fa';

    const Mine = () => {
      const { isConnected, connectWallet } = useWallet();
      const { stats, mineTokens, txLoading, userStats } = useDolphin();
      const [amount, setAmount] = useState('');
      const [estimatedTokens, setEstimatedTokens] = useState(0);
      const [uiError, setUiError] = useState('');
      const [txSuccess, setTxSuccess] = useState(false);
      const [txHash, setTxHash] = useState('');
      const unlockedLevels = userStats?.levels || [];
      const maxUnlocked = unlockedLevels.length > 0 ? Math.max(...unlockedLevels) : 0;
      const hasLevel = unlockedLevels.length > 0;

      const navigate = useNavigate();
      if (!isConnected && localStorage.getItem('walletPromptSkipped') !== 'true') {
        return <ConnectPrompt />;
      }

      const calculateTokens = (usdAmount) => {
        if (!usdAmount || isNaN(usdAmount) || parseFloat(stats?.currentPrice) === 0) return 0;
        return parseFloat(usdAmount) / parseFloat(stats.currentPrice);
      };

      const formatUsdPrice = (value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return '$0.00';
        return `$${numericValue.toFixed(2)}`;
      };

      // Remove trailing zeros and unnecessary decimals for Est
      const formatTokenAmount = (val) => {
        if (isNaN(val)) return '0';
        // Show up to 4 decimals, but trim trailing zeros
        return parseFloat(Number(val).toFixed(4)).toString();
      };

      const handleAmountChange = (e) => {
        const value = e.target.value;
        setAmount(value);
        setEstimatedTokens(calculateTokens(value));
      };

      const handleMine = async () => {
        setUiError('');
        setTxSuccess(false);
        setTxHash('');
        if (!isConnected) {
          toast.info('Connecting wallet...');
          await connectWallet();
          if (!isConnected) {
            setUiError('Wallet not connected. Please connect your wallet to mine tokens.');
            return;
          }
        }
        if (!amount || parseFloat(amount) <= 0) {
          setUiError('Please enter a valid amount');
          return;
        }
        if (parseFloat(amount) < 100) {
          setUiError('Minimum mining amount is $100');
          return;
        }
        if (!hasLevel) {
          setUiError('You must own at least one level to mine DOLPCOIN.');
          return;
        }
        try {
          const result = await mineTokens(amount);
          if (result && result.receipt && result.receipt.status === 1) {
            setTxSuccess(true);
            setTxHash(result.receipt.transactionHash || '');
            setAmount('');
            setEstimatedTokens(0);
          } else {
            setUiError('Transaction was not confirmed or was rejected.');
          }
        } catch (err) {
          setUiError(err.message || 'Failed to mine tokens');
        }
      };

      if (stats?.miningEnded) {
        const handleGoToClaim = (e) => {
          e.preventDefault();
          if (navigate) {
            navigate('/claim');
          } else {
            window.location.href = '/claim';
          }
        };
        return (
          <div className="flex flex-col p-4 bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
            <span className="text-4xl mb-2">⛔</span>
            <h2 className="text-lg font-bold mb-1 text-gray-800 dark:text-gray-100">Mining Ended</h2>
            <p className="text-sm text-gray-500 mb-3 max-w-xs">Claim your tokens below.</p>
            <button onClick={handleGoToClaim} className="px-4 py-2 rounded-lg bg-cyan-600 text-white font-semibold text-sm shadow hover:bg-cyan-700 transition">Go to Claim</button>
          </div>
        );
      }

      return (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 border border-gray-700 shadow-xl w-full flex flex-col gap-8 sm:gap-12 md:gap-16 lg:gap-20">
          <div className="w-full mb-8 sm:mb-12">
            <h2 className="text-base sm:text-lg font-bold mb-1 flex items-center text-gray-100">
              <FaHammer className="mr-2 text-cyan-400 text-xl" /> Mine DOLP
            </h2>
            <div className="rounded-md border border-cyan-200 dark:border-cyan-900 bg-cyan-50 dark:bg-cyan-950/30 px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-200 mb-2 flex gap-4">
              <div><b>Level:</b> {maxUnlocked > 0 ? `L${maxUnlocked}` : 'None'}</div>
              <div><b>Next:</b> {maxUnlocked < 12 ? `L${maxUnlocked+1}` : 'Max'}</div>
            </div>
          </div>
          {txSuccess ? (
            <div className="w-full flex flex-col bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md p-6 mb-2">
              <div className="text-3xl mb-3 text-emerald-600 dark:text-emerald-300">🎉</div>
              <div className="text-lg font-semibold text-emerald-700 dark:text-emerald-200 mb-2">Mined {estimatedTokens.toFixed(4)} DOLP!</div>
              {txHash && (
                <a href={`https://polygonscan.com/tx/${txHash}`} target="_blank" rel="noopener noreferrer" className="text-sm text-cyan-600 dark:text-cyan-300 underline mb-2">View Tx</a>
              )}
              <button className="mt-2 px-4 py-2 rounded bg-cyan-600 text-white text-base font-semibold shadow hover:bg-cyan-700" onClick={() => setTxSuccess(false)}>Mine More</button>
            </div>
          ) : (
            <div className="w-full flex flex-col gap-3">
              <div className="flex gap-2 w-full bg-cyan-100/40 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-800 rounded-md px-3 py-2 mb-1 text-base text-cyan-800 dark:text-cyan-200">
                <FaInfoCircle className="text-cyan-400 mr-2 text-xl" />
                <span>Min: <b>100 USDT</b>. Need <b>1+ level</b>.</span>
              </div>
              <label htmlFor="amount" className="block font-semibold mb-1 text-base text-gray-200">Amount (USDT)</label>
              <input
                id="amount"
                type="number"
                min="0"
                step="any"
                value={amount}
                onChange={handleAmountChange}
                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-base focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="USDT amount"
              />
              {uiError && <div className="text-red-500 text-xs mb-1">{uiError}</div>}
              <div className="flex text-xs text-gray-400 mb-1">
                <span className="mr-4">Price: <span className="text-cyan-400 font-semibold">{formatUsdPrice(stats?.currentPrice)}</span></span>
                <span>Est: <span className="text-purple-400 font-semibold">{formatTokenAmount(estimatedTokens)} DOLP</span></span>
              </div>
              <button
                className="w-full py-1.5 mt-1 rounded-lg bg-cyan-600 text-white font-semibold text-sm shadow hover:bg-cyan-700 transition disabled:opacity-60"
                onClick={handleMine}
                disabled={txLoading}
              >
                {txLoading ? 'Mining...' : 'Mine'}
              </button>
            </div>
          )}
        </div>
      );
    };

    export default Mine;

